import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import clsx from 'clsx';

import { useAppSelector } from 'redux/store';
import { setCurrentPage, setInConvoWithArticles, setTotalInConvoWithArticles } from 'redux/stores/in-convo-with';
import client from 'api/apollo-client';
import { getServerPageGetOpOostInConvoWithArticles, getServerPageGetThemes } from 'api/graphql-helper';
import { OpOostInConvoWithOverviewFieldsFragment, Theme } from 'api/graphql-types';

import { Button } from 'components/button';
import { usePrevious } from 'utils/use-previous';
import { FilterButton, IFilterItem } from 'components/filter-button/filter-button';
import { CardNews, CardNewsType } from 'components/card';
import { Pagination } from 'components/pagination';
import { EmptyState } from 'components/empty-state/empty-state';
import { getInitialFilterItem } from 'utils/get-initial-filter-item';

import styles from './in-convo-with-overview.module.scss';

export interface IInConvoWithOverviewProps {
	module: OpOostInConvoWithOverviewFieldsFragment;
}

const getInConvoWithUrl = (): CardNewsType => {
	return 'in-gesprek-met';
};

const InConvoWithOverview = (props: IInConvoWithOverviewProps): React.JSX.Element => {
	const { publicationState } = useAppSelector((state) => state.settings);
	const dispatch = useDispatch();
	const intl = useIntl();
	const router = useRouter();

	const overviewRef = useRef<HTMLDivElement>(null);
	const [themes, setThemes] = useState<Theme[]>([]);
	const [initialThemes, setInitialThemes] = useState<IFilterItem[]>([]);
	const [selectedThemes, setSelectedThemes] = useState<IFilterItem[]>(initialThemes);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const { inConvoWithArticles, totalInConvoWithArticles, currentPage } = useAppSelector((state) => state.inConvoWith);
	const { contentType, isSubsite } = useAppSelector((state) => state.settings);

	useEffect(() => {
		getThemes();
	}, []);

	const prevCurrentPage = usePrevious(currentPage);
	const prevInitialThemes = usePrevious(initialThemes);

	useEffect(() => {
		if (currentPage !== prevCurrentPage && prevCurrentPage !== null) {
			getInConvoWithArticles(
				currentPage,
				selectedThemes.map((item) => String(item.id))
			);
		}
	}, [currentPage]);

	useEffect(() => {
		if (
			currentPage &&
			initialThemes &&
			prevInitialThemes !== null &&
			initialThemes?.length !== prevInitialThemes?.length
		) {
			getInConvoWithArticles(
				currentPage,
				initialThemes.map((item) => String(item.id))
			);
		}
		if (selectedThemes.length === 0 && initialThemes) {
			setSelectedThemes(initialThemes);
		}
	}, [initialThemes]);

	const getThemes = async (): Promise<void> => {
		const result = await (
			await getServerPageGetThemes({ variables: { locale: router.locale } }, client)
		).props.data.themes;

		if (result) {
			const newThemes = result as Theme[];
			setThemes(newThemes);
			setInitialThemes(
				getInitialFilterItem(
					newThemes.map((item) => {
						return {
							id: item.documentId || '',
							title: item.attributes?.title || ''
						};
					}),
					router?.query?.themeIds
				)
			);
		}
	};

	const getInConvoWithArticles = async (page: number, themeIds: string[]): Promise<void> => {
		setIsLoading(true);
		const start = (page - 1) * 9;

		const variables = {
			publicationState,
			limit: 9,
			start,
			sort: ['firstPublishedAt:desc'],
			themeIds: themeIds.length === 0 ? undefined : themeIds.map((item) => item.toString()),
			locale: router.locale
		};

		let inConvoWithArticleCount;
		let inConvoWithArticlesData;

		if (contentType === 'op-oost-in-convo-with-overview') {
			const inConvoWithArticlesResult = await getServerPageGetOpOostInConvoWithArticles(
				{
					variables
				},
				client
			);
			inConvoWithArticleCount =
				inConvoWithArticlesResult.props.data.opOostInConvoWithArticles_connection?.pageInfo?.total;
			inConvoWithArticlesData = inConvoWithArticlesResult.props.data.opOostInConvoWithArticles_connection?.data?.map(
				(item: any) => item.attributes
			);
		}

		if (inConvoWithArticlesData) {
			dispatch(setInConvoWithArticles(inConvoWithArticlesData));
			dispatch(setTotalInConvoWithArticles(Number(inConvoWithArticleCount)));
		}
		setIsLoading(false);
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			if (typeof window !== 'undefined') {
				const extraOffset = 144;
				const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.pageYOffset - extraOffset;
				window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
			}

			dispatch(setCurrentPage(page));
		}
	};

	const handleFilterChange = (): void => {
		getInConvoWithArticles(
			1,
			selectedThemes.map((item) => String(item.id))
		);
	};

	return (
		<div className={styles.InConvoWithOverview} ref={overviewRef}>
			<div className={styles.wrapper}>
				<div className={clsx(styles.filterBar, isSubsite && styles.justifyRight)}>
					{!isSubsite && (
						<div className={styles.filterContent}>
							<span className={styles.label}>{props.module.filterLabel}</span>
							<div className={styles.filterButtons}>
								<span className={styles.themeFilter}>
									<FilterButton
										title={intl.formatMessage({ id: 'modules.inConvoWithOverview.themeFilter.title' })}
										items={
											themes?.map((theme) => ({ id: theme.documentId || '', label: theme?.attributes?.title || '' })) ||
											[]
										}
										onChange={handleFilterChange}
										onSelectedItemsChanged={setSelectedThemes}
										selectedItems={selectedThemes}
									/>
								</span>
								{!!selectedThemes.length && (
									<span className={styles.removeFilters}>
										<Button
											title={intl.formatMessage(
												{ id: 'modules.inConvoWithOverview.deleteFilters' },
												{ amount: selectedThemes.length }
											)}
											variant="tertiary-dark"
											onClick={() => {
												setSelectedThemes([]);
												getInConvoWithArticles(1, [] as string[]);
											}}
										/>
									</span>
								)}
							</div>
						</div>
					)}

					<div className={styles.amountOfResults}>
						{intl.formatMessage({ id: 'global.results' }, { results: totalInConvoWithArticles })}
					</div>
				</div>
				<div
					className={styles.content}
					style={{
						opacity: isLoading ? '0.2' : 1,
						height: inConvoWithArticles?.length === 9 ? '1350px' : '300px'
					}}
				>
					{inConvoWithArticles?.length > 0 ? (
						inConvoWithArticles?.map((article, index) => (
							<div key={index}>
								<CardNews
									id={`in-convo-with-overview-${index}`}
									bodyText={article?.bodyText?.bodyText || ''}
									excerpt={article?.header?.excerpt || ''}
									publishedAt={article?.firstPublishedAt}
									slug={article?.slug || ''}
									title={article?.title || ''}
									cover={article?.header?.cover?.data?.attributes}
									key={index}
									type={getInConvoWithUrl()}
									priority={index < 3}
								/>
							</div>
						))
					) : (
						<div className={styles.emptyState}>
							<EmptyState isLoading={isLoading} />
						</div>
					)}
				</div>
			</div>
			<div className={styles.pagination}>
				<Pagination
					currentPage={currentPage}
					totalPages={Math.ceil(totalInConvoWithArticles / 9)}
					onPageChange={handlePageChange}
				/>
			</div>
		</div>
	);
};

export default InConvoWithOverview;
